import { create } from 'zustand';

// Raw phoenix Presence state per team (`{ [userId]: { metas: [...] } }`), not
// flattened — board filtering needs to check every meta (a user connected
// from two devices may have only one of them on the board in question).
export const useTeamPresenceStore = create((set) => ({
  byTeam: {},
  setTeamPresence: (teamId, state) =>
    set((s) => ({ byTeam: { ...s.byTeam, [teamId]: state } })),
  clearTeamPresence: (teamId) =>
    set((s) => {
      const byTeam = { ...s.byTeam };
      delete byTeam[teamId];
      return { byTeam };
    }),
}));

/** Members of `teamId`'s roster whose metas show them viewing `boardId`, excluding `excludeUserId`. */
export function selectBoardViewers(byTeam, teamId, boardId, excludeUserId) {
  if (teamId == null || boardId == null) return [];
  const state = byTeam?.[teamId] || {};
  const out = [];
  Object.entries(state).forEach(([userId, entry]) => {
    if (String(userId) === String(excludeUserId)) return;
    const metas = entry?.metas || [];
    const onBoard = metas.some((m) => m.board_id != null && String(m.board_id) === String(boardId));
    if (!onBoard) return;
    const meta = metas[0] || {};
    out.push({ userId, name: meta.name || `User ${userId}` });
  });
  return out;
}

/** Members of `teamId`'s roster whose metas show them viewing `taskId`, excluding `excludeUserId`. */
export function selectTaskViewers(byTeam, teamId, taskId, excludeUserId) {
  if (teamId == null || taskId == null) return [];
  const state = byTeam?.[teamId] || {};
  const out = [];
  Object.entries(state).forEach(([userId, entry]) => {
    if (String(userId) === String(excludeUserId)) return;
    const metas = entry?.metas || [];
    const onTask = metas.some((m) => m.task_id != null && String(m.task_id) === String(taskId));
    if (!onTask) return;
    const meta = metas[0] || {};
    out.push({ userId, name: meta.name || `User ${userId}` });
  });
  return out;
}

/** Members of `teamId`'s roster whose metas show them viewing `documentId`, excluding `excludeUserId`. */
export function selectDocumentViewers(byTeam, teamId, documentId, excludeUserId) {
  if (teamId == null || documentId == null) return [];
  const state = byTeam?.[teamId] || {};
  const out = [];
  Object.entries(state).forEach(([userId, entry]) => {
    if (String(userId) === String(excludeUserId)) return;
    const metas = entry?.metas || [];
    const onDocument = metas.some((m) => m.document_id != null && String(m.document_id) === String(documentId));
    if (!onDocument) return;
    const meta = metas[0] || {};
    out.push({ userId, name: meta.name || `User ${userId}` });
  });
  return out;
}

/**
 * Block-level ("which user is on which line") presence within one document or
 * task: every roster meta that carries a block_id AND matches the given
 * entity ({ documentId } or { taskId } — exactly one is expected), one row
 * per matching meta (not deduped by user — a user connected from two devices
 * on two different blocks shows up twice, which is the point: block presence
 * tracks the connection, not the person). Excludes `excludeUserId`.
 *
 * `blockOffset` mirrors `meta.block_offset` (the character offset the
 * caret-follow presence avatar renders at) as-is — undefined/null when the
 * meta doesn't carry one, in which case the renderer falls back to
 * end-of-block.
 */
export function selectBlockPresence(byTeam, teamId, { documentId, taskId } = {}, excludeUserId) {
  if (teamId == null || (documentId == null && taskId == null)) return [];
  const state = byTeam?.[teamId] || {};
  const out = [];
  Object.entries(state).forEach(([userId, entry]) => {
    if (String(userId) === String(excludeUserId)) return;
    const metas = entry?.metas || [];
    metas.forEach((m) => {
      if (m.block_id == null) return;
      const matches =
        (documentId != null && m.document_id != null && String(m.document_id) === String(documentId)) ||
        (taskId != null && m.task_id != null && String(m.task_id) === String(taskId));
      if (!matches) return;
      out.push({ userId, name: m.name || `User ${userId}`, blockId: m.block_id, blockOffset: m.block_offset });
    });
  });
  return out;
}
